import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { evaluateEntitlements } from '../entitlements/evaluate';

// Live view of what the signed-in account may do (tier, storage pool,
// monthly credits, watermark removal, retention, seats). Subscribes to
// users/{uid} and applies the same grace-expiry + missing-doc semantics as
// the server (see src/entitlements/evaluate.js). Signed-out → Free defaults.
//
// Also returns the raw `subscription` map off the same snapshot (#350): the
// upsell prompts have to tell "Free, never subscribed" (offer a checkout) from
// "lapsed / past-due past its grace window" (tier reads free, but the server
// still refuses a second subscription — those accounts belong in the portal).
// Reading it here rather than adding useBillingSummary alongside keeps
// consumers to ONE listener on a document both hooks already watch.
// `watermarkDefaults` rides the same snapshot for the same reason: the
// account's last-used watermark customization ({logoId, position, size,
// opacity}, written by generate_edited_video) seeds each new edit's watermark
// card, and it lives on the very document this hook already watches.
const useEntitlements = (user) => {
  const [entitlements, setEntitlements] = useState(() => evaluateEntitlements(null, 0));
  const [subscription, setSubscription] = useState(null);
  const [watermarkDefaults, setWatermarkDefaults] = useState(null);
  const [loading, setLoading] = useState(true);

  // Keyed on the uid, not the user object: callers pass whatever their auth
  // hook hands them, and an identity-unstable `user` would otherwise tear down
  // and re-open the Firestore listener on every render.
  const uid = user ? user.uid : null;

  useEffect(() => {
    if (!uid) {
      setEntitlements(evaluateEntitlements(null, 0));
      setSubscription(null);
      setWatermarkDefaults(null);
      setLoading(false);
      return;
    }

    // Re-raise `loading` for the null -> uid transition. Auth restores a
    // session asynchronously, so the first render of a signed-in visit always
    // passes through `uid === null`, which resolved above to `loading: false`
    // + Free defaults. Without this, `loading` would stay false while a
    // Solo/Pro account still reads as Free, and every consumer that
    // distinguishes "known Free" from "not known yet" — the #350 upsell
    // prompts especially — would pitch an upgrade to a paying customer.
    setLoading(true);

    const userRef = doc(db, 'users', uid);
    const unsubscribe = onSnapshot(userRef, (snapshot) => {
      const data = snapshot.exists() ? snapshot.data() : null;
      setEntitlements(evaluateEntitlements(data, Date.now() / 1000));
      setSubscription((data && data.subscription) || null);
      setWatermarkDefaults((data && data.watermarkDefaults) || null);
      setLoading(false);
    }, (error) => {
      console.error('Error listening to entitlements:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [uid]);

  return { ...entitlements, subscription, watermarkDefaults, loading };
};

export default useEntitlements;
